import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Empty, Divider, Button, Spin, Badge } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Room, Message } from '../../types/chat';
import MessageGroup from './MessageGroup';
import { groupMessagesByDate, groupConsecutiveMessages, getVisibleMessages } from './utils';
import { BASE_URL } from '../../config';
import '../styles/MessageList.css';
import { useTranslation } from 'react-i18next';

interface MessageListProps {
  room: Room;
  onRoomUpdate?: (roomId: string, updater: (messages: Message[]) => Message[]) => void;
  webSocket?: WebSocket | null;
  onReplyMessage?: (message: Message) => void;
  onEditMessage?: (message: Message) => void;
  highlightedMessageId?: string | null;
}

const MessageList: React.FC<MessageListProps> = ({ room, onRoomUpdate, webSocket, onReplyMessage, onEditMessage, highlightedMessageId }) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  
  // Состояние для кнопки "Вниз"
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Состояние для отслеживания доступности более старой истории
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [usingDeepHistory, setUsingDeepHistory] = useState(false);
  
  // Используем ref для хранения текущего состояния загрузки
  // чтобы избежать проблем с замыканиями в эффектах
  const isLoadingOlderRef = useRef(false);
  
  // Идентификатор последнего сообщения в комнате для отслеживания новых сообщений
  const lastMessageIdRef = useRef<string | null>(null);
  
  // Признак первой загрузки комнаты
  const isFirstLoadRef = useRef(true);
  
  // Флаг для отслеживания первой загрузки истории
  const isInitialHistoryLoadRef = useRef(true);
  
  // Переменная для хранения комнаты между загрузками
  const previousRoomIdRef = useRef<string | null>(null);

  // Сброс состояния при смене комнаты
  useEffect(() => {
    if (previousRoomIdRef.current !== room.id) {
      setHasMoreHistory(true);
      setUsingDeepHistory(false);
      previousRoomIdRef.current = room.id;
      isInitialHistoryLoadRef.current = true; // Сбрасываем флаг при смене комнаты
    }
  }, [room.id]);

  // Функция для проверки, добавлены ли новые сообщения снизу
  const hasNewMessagesAtBottom = useCallback(() => {
    if (!room.messages || room.messages.length === 0) return false;
    
    const lastMessage = room.messages[room.messages.length - 1];
    
    // Если ID последнего сообщения изменился, значит добавилось новое сообщение
    if (lastMessageIdRef.current !== null && 
        lastMessage.created !== lastMessageIdRef.current) {
      return true;
    }
    
    return false;
  }, [room.messages]);

  // Function to load more messages using regular pagination
  const loadMoreMessagesRegular = async (): Promise<Message[]> => {
    const token = localStorage.getItem('token');
    
    // Находим самое старое сообщение в текущей истории по timestamp
    const oldestMessage = room.messages.reduce((oldest, msg) => {
      return (!oldest || msg.created < oldest.created) ? msg : oldest;
    }, room.messages[0]);
    
    const oldestTimestamp = oldestMessage?.created || Date.now();
    
    console.log(`📅 Загрузка сообщений старше ${new Date(oldestTimestamp).toLocaleString()}`);
    
    // Передаем timestamp самого старого сообщения для корректной загрузки
    const response = await fetch(`${BASE_URL}/load-more/${room.id}/${room.messages.length}?oldestTimestamp=${oldestTimestamp}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to load more messages');
    }
    
    return await response.json();
  };

  // Function to load older history when regular pagination is exhausted
  const loadOlderHistory = async (): Promise<{ messages: Message[], hasMore: boolean }> => {
    const token = localStorage.getItem('token');
    
    // Находим самое старое сообщение в текущей истории по timestamp
    const oldestMessage = room.messages.reduce((oldest, msg) => {
      return (!oldest || msg.created < oldest.created) ? msg : oldest;
    }, room.messages[0]);
    
    const oldestTimestamp = oldestMessage?.created || Date.now();
    
    console.log(`🕰️ Глубокая загрузка истории старше ${new Date(oldestTimestamp).toLocaleString()}`);
    
    // Передаем timestamp самого старого сообщения для корректной загрузки
    const response = await fetch(`${BASE_URL}/load-older-history/${room.id}/${room.messages.length}?oldestTimestamp=${oldestTimestamp}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to load older history');
    }
    
    const data = await response.json();
    return {
      messages: data.messages || [],
      hasMore: data.hasMore || false
    };
  };

  // Main function to load more messages
  const loadMoreMessages = useCallback(async () => {
    if (loading || !room?.id || !room.messages.length || !hasMoreHistory) return;
    
    setLoading(true);
    isLoadingOlderRef.current = true;
    
    try {
      // Сохраняем текущую позицию прокрутки
      let scrollPosition = 0;
      let scrollHeight = 0;
      
      if (containerRef.current) {
        scrollPosition = containerRef.current.scrollTop;
        scrollHeight = containerRef.current.scrollHeight;
      }
      
      let newMessages: Message[] = [];
      let shouldTryDeepHistory = false;
      
      if (!usingDeepHistory) {
        // Сначала пытаемся загрузить через обычную пагинацию
        console.log('🔄 Загрузка через обычную пагинацию...');
        
        try {
          newMessages = await loadMoreMessagesRegular();
          
          // Если получили мало сообщений (меньше 10), переключаемся на глубокую историю
          if (newMessages.length < 10) {
            console.log('⚡ Обычная пагинация исчерпана, переключаемся на глубокую историю');
            shouldTryDeepHistory = true;
            setUsingDeepHistory(true);
          }
        } catch (error) {
          console.error('Ошибка обычной пагинации:', error);
          shouldTryDeepHistory = true;
          setUsingDeepHistory(true);
        }
      } else {
        shouldTryDeepHistory = true;
      }
      
      // Если нужно, пытаемся загрузить через глубокую историю
      if (shouldTryDeepHistory || usingDeepHistory) {
        console.log('🔍 Загрузка через глубокую историю...');
        
        try {
          const historyResult = await loadOlderHistory();
          newMessages = historyResult.messages;
          setHasMoreHistory(historyResult.hasMore);
          
          if (!historyResult.hasMore) {
            console.log('📭 Достигнуто конец истории сообщений');
          }
        } catch (error) {
          console.error('Ошибка загрузки глубокой истории:', error);
          setHasMoreHistory(false);
        }
      }
      
      // Update room with new messages (add them to the beginning)
      if (newMessages.length > 0) {
        console.log(`✅ Загружено ${newMessages.length} сообщений`);
        
        // Обновляем сообщения
        if (onRoomUpdate) {
          onRoomUpdate(room.id, (existingMessages) => {
            return [...newMessages, ...existingMessages];
          });
        } else {
          room.messages = [...newMessages, ...room.messages];
        }
        
        // После обновления DOM отложенно восстанавливаем позицию прокрутки
        requestAnimationFrame(() => {
          if (containerRef.current) {
            // Вычисляем разницу высоты контента и устанавливаем скролл в соответствующую позицию
            const newScrollHeight = containerRef.current.scrollHeight;
            const newScrollPosition = scrollPosition + (newScrollHeight - scrollHeight);
            
            containerRef.current.scrollTop = newScrollPosition;
            console.log('📍 Восстановлен скролл после загрузки:', 
              { было: scrollPosition, стало: newScrollPosition, 
                высотаБыла: scrollHeight, высотаСтала: newScrollHeight });
          }
        });
      } else if (!usingDeepHistory) {
        // Если не получили сообщений через обычную пагинацию, пробуем глубокую историю
        setUsingDeepHistory(true);
      } else {
        // Если не получили сообщений через глубокую историю, значит достигли конца
        setHasMoreHistory(false);
        console.log('🏁 Достигнуто конец истории сообщений');
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
      setHasMoreHistory(false);
    } finally {
      setLoading(false);
      
      // Устанавливаем таймер для сброса флага загрузки
      setTimeout(() => {
        isLoadingOlderRef.current = false;
      }, 200);
    }
  }, [room, loading, onRoomUpdate, hasMoreHistory, usingDeepHistory]);

  // Function to handle message deletion
  const handleDeleteMessage = useCallback((roomId: string, eventId: string) => {
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
      console.error('WebSocket не подключен');
      return;
    }

    const deleteMessage = {
      type: 'delete_message',
      roomId,
      eventId
    };

    webSocket.send(JSON.stringify(deleteMessage));
  }, [webSocket]);

  // Function to handle message reply
  const handleReplyMessage = useCallback((message: Message) => {
    if (onReplyMessage) {
      onReplyMessage(message);
    }
  }, [onReplyMessage]);

  // Отслеживание скролла для показа кнопки "Вниз"
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // Показываем кнопку если прокрутили больше чем на 300px от низа
      setShowScrollButton(distanceFromBottom > 300);
      
      // Подсчитываем непрочитанные сообщения (условно, можно доработать)
      if (distanceFromBottom > 300) {
        // Здесь можно добавить логику подсчета новых сообщений
        setUnreadCount(0); // Пока просто 0
      } else {
        setUnreadCount(0);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Функция для прокрутки вниз
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Обработка изменения сообщений или смены комнаты
  useEffect(() => {
    // Если комната пуста, нет смысла делать что-либо
    if (!room.messages || room.messages.length === 0) {
      return;
    }
    
    // Получаем последнее сообщение
    const lastMessage = room.messages[room.messages.length - 1];
    
    // Проверяем, сменилась ли комната
    const isRoomChanged = previousRoomIdRef.current !== room.id;
    
    // Проверяем, является ли это первой загрузкой истории
    const isInitialHistory = isInitialHistoryLoadRef.current && room.messages.length > 0;
    
    if (isRoomChanged || isInitialHistory) {
      console.log('🏠 Комната сменилась или первая загрузка истории:', 
        { было: previousRoomIdRef.current, стало: room.id, первая_загрузка: isInitialHistory });
      
      // Обновляем ссылку на текущую комнату
      previousRoomIdRef.current = room.id;
      
      // При первой загрузке или смене комнаты сразу скроллим вниз
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
          console.log('📍 Скролл до конца при первой загрузке или смене комнаты');
        }
      });
      
      // Сбрасываем флаги
      isFirstLoadRef.current = false;
      isInitialHistoryLoadRef.current = false;
    } 
    // Если не загружаются старые сообщения и добавилось новое сообщение снизу
    else if (!isLoadingOlderRef.current && hasNewMessagesAtBottom()) {
      console.log('📍 Скролл вниз для нового сообщения');
      
      // Отложенно скроллим до конца, если пришло новое сообщение
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      });
    }
    
    // Обновляем ID последнего сообщения
    lastMessageIdRef.current = lastMessage.created;
  }, [room.id, room.messages, hasNewMessagesAtBottom]);

  if (!room.messages || room.messages.length === 0) {
    return <Empty description="No messages yet" />;
  }

  // Фильтруем сообщения для отображения, исключая m.reaction и m.room.redaction
  const visibleMessages = getVisibleMessages(room.messages);

  // Group messages by date (используем только видимые сообщения для отображения)
  const groupedMessagesByDate = groupMessagesByDate(visibleMessages);

  return (
    <div className="messages-wrapper-container">
      <div 
        className="messages-container" 
        ref={containerRef}
      >
        <div className="load-more-container">
          {loading ? (
            <Spin tip={usingDeepHistory ? t('messageList.loadingDeep') : t('messageList.loading')} />
          ) : hasMoreHistory ? (
            <Button 
              onClick={loadMoreMessages} 
              type="link"
              disabled={loading}
            >
              {usingDeepHistory ? t('messageList.loadMoreDeep') : t('messageList.loadMore')}
            </Button>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '10px' }}>
              {t('messageList.reachedStartOfHistory')}
            </div>
          )}
        </div>
        
        {Object.entries(groupedMessagesByDate).map(([date, messages]) => {
          // Group consecutive messages by same sender within time threshold
          const consecutiveGroups = groupConsecutiveMessages(messages);
          
          return (
            <React.Fragment key={date}>
              <Divider plain className="date-divider">
                {date}
              </Divider>
              
              {consecutiveGroups.map((messageGroup, idx) => (
                <MessageGroup 
                  key={idx} 
                  messages={messageGroup} 
                  allMessages={room.allMessages || room.messages} // Используем все события если доступны, иначе fallback
                  onDeleteMessage={handleDeleteMessage}
                  onReplyMessage={handleReplyMessage}
                  onEditMessage={onEditMessage}
                  webSocket={webSocket}
                  highlightedMessageId={highlightedMessageId}
                />
              ))}
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Кнопка "Вниз" для быстрой прокрутки - вне контейнера скролла */}
      {showScrollButton && (
        <div className="scroll-to-bottom-button">
          <Badge count={unreadCount} offset={[-5, 5]}>
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<DownOutlined />}
              onClick={scrollToBottom}
              className="scroll-button"
            />
          </Badge>
        </div>
      )}
    </div>
  );
};

export default MessageList; 